import { interpolate, splitPathString, interpolateAll, separate, combine, toCircle } from "flubber";

type Interpolator = (time: number) => string;

export class MorphPath {
    public isEndPath: boolean = false;
    public outputPath: SVGPathElement;
    public pathColor: string;

    private interpolator: Interpolator;

    constructor(firstPath: SVGPathElement, secondPath: SVGPathElement) {
        if(!firstPath && !secondPath) {
            console.warn("Both paths are null, can not construct meaningful MorphPath object");
            return this;
        }

        // Get segments (d) from path elements
        let firstSegments = this.getSvgSegments(firstPath);
        let secondSegments = this.getSvgSegments(secondPath);

        this.pathColor = this.getSvgPathColor(secondPath) || 'currentColor';

        // console.log("Create new interpolator ", firstPath, secondPath, firstSegments, secondSegments);
        // Generate morphing interpolator between segments in paths
        this.interpolator = this.getInterpolator(firstSegments, secondSegments);
    }

    /**
     * Calculate the interpolation of segments (morph between first and second path) 
     * at the specified time between 0 and 1 
     */
    public render(time: number): string {
        return this.interpolator(time);
    }

    /**
    * Get the SVG path (d) attribute from a SVG HTML element
    */
    private getSvgSegments(svgPath: SVGPathElement): string {
        if(!svgPath) return '';
        // Select the first path of the SVG element
        let segments = svgPath.getAttribute('d');
        return segments || '';
    }

    /**
    * Get the SVG path fill color
    */
    private getSvgPathColor(svgPath: SVGPathElement): string {
        if(!svgPath) return '';
        return svgPath.getAttribute('fill') || '';
    }

    /**
    * Detect which interpolator strategy to use depending on the number 
    * of path segments in the first (current) and second (next) svg.
    */
    private getInterpolator(firstSegment: string, secondSegment: string): Interpolator {
        let firstSegments: string[] = splitPathString(firstSegment);
        let secondSegments: string[] = splitPathString(secondSegment);

        // From one to one path interpolator
        if(firstSegments.length == 1 && secondSegments.length == 1) {
            return interpolate(firstSegment, secondSegment);
        }

        // From one to many interpolator
        if(firstSegments.length == 1 && secondSegments.length > 1) {
            return separate(firstSegment, secondSegments, { single: true });
        }

        // From many to one
        if(firstSegments.length > 1 && secondSegments.length == 1) {
            return combine(firstSegments, secondSegment, { single: true });
        }

        // From many to many paths that equal in length
        if(firstSegments.length == secondSegments.length) {
            // If paths are equal, interpolate all of them
            return interpolateAll(firstSegments, secondSegments, { single: true });
        }

        // From many to many that has unequal length
        return this.getUnequalInterpolator(firstSegments, secondSegments);
    }


    /**
    * Generate interpolator between paths with unequal part lengths
    */
    private getUnequalInterpolator(firstSegments: string[], secondSegments: string[]): Interpolator {
        // Detect if fist or second path is shortest
        let [shortPath, longPath] = (firstSegments.length < secondSegments.length) ? [firstSegments, secondSegments] : [secondSegments, firstSegments];
        // Generate a list of paths missing a counter part in second and first svg
        let missingSegments = longPath.slice(shortPath.length);

        // Generate a circle path for all missing paths 
        for(let missingSegment of missingSegments) {
            // Add paths to shortest path
            shortPath.push(this.generateMissingPart(missingSegment));
        }
        // Interpolate between all paths in first and second svg
        return interpolateAll(firstSegments, secondSegments, { single: true });
    }

    /**
    * Generate a svg circle path at the position of an svg path
    */
    private generateMissingPart(missingSegment: string): string {
        // Get start position of path (Mxxx.xxx xxx.xxx)
        let positionReg = new RegExp(/M(([0-9]*[\.]*[0-9]*)) ([0-9]*[\.]*[0-9]*)/g).exec(missingSegment);
        // Get start position from regex
        let x = (positionReg && positionReg.length >= 3) ? parseFloat(positionReg[2]) : 0;
        let y = (positionReg && positionReg.length >= 3) ? parseFloat(positionReg[3]) : 0;

        // Generate a interpolator
        let interpolator = toCircle(missingSegment, x, y, 0);
        // Render the dot as if it was interpolated a time 1
        return interpolator(1);
    }
}
